// Path — the Timeline-scene landmark. A TubeGeometry following the
// TIMELINE_PATH CatmullRomCurve3 at radius 1.6, with 6 small glowing
// points at the milestone positions. Tube color: warm white with low
// emissive. Milestone markers: small octahedrons (radius 0.06) with
// emissive #b8a572 (gold).

import { useMemo } from 'react';
import * as THREE from 'three';

import {
  TIMELINE_MILESTONES,
  TIMELINE_PATH,
  timelinePointAt,
} from '../planetSections';

const TUBE_RADIUS = 0.025;
const TUBE_SEGMENTS = 128;
const TUBE_RADIAL = 6;
const MILESTONE_RADIUS = 0.06;
const TUBE_COLOR = '#f5f0e8'; // warm white
const MILESTONE_COLOR = '#b8a572'; // gold
const ACTIVE_MILESTONE_COLOR = '#7fb89f'; // mint for active

export interface PathProps {
  isActive?: boolean;
  /** Index of the currently active milestone (-1 for none). */
  activeMilestoneIndex?: number;
}

export function Path({
  isActive = false,
  activeMilestoneIndex = -1,
}: PathProps) {
  // Tube geometry — built once.
  const tubeGeometry = useMemo(() => {
    return new THREE.TubeGeometry(
      TIMELINE_PATH,
      TUBE_SEGMENTS,
      TUBE_RADIUS,
      TUBE_RADIAL,
      true,
    );
  }, []);

  // Milestone positions — sampled once, memoized.
  const milestonePositions = useMemo(() => {
    return TIMELINE_MILESTONES.map((m) => {
      const v = timelinePointAt(m.positionOnPath);
      return new THREE.Vector3(v.x, v.y, v.z);
    });
  }, []);

  return (
    <group name="landmark-path">
      {/* The tube itself — warm white with low emissive. */}
      <mesh geometry={tubeGeometry} name="path-tube" raycast={() => null}>
        <meshStandardMaterial
          color={TUBE_COLOR}
          emissive={TUBE_COLOR}
          emissiveIntensity={isActive ? 0.5 : 0.25}
          transparent
          opacity={0.75}
          roughness={0.6}
        />
      </mesh>

      {/* Milestone markers — octahedrons at each TIMELINE_MILESTONES
          position. Active one glows mint instead of gold. */}
      {milestonePositions.map((pos, i) => {
        const isActiveMilestone = i === activeMilestoneIndex;
        return (
          <group key={i} position={pos} name={`milestone-${i}`}>
            <mesh
              name={`milestone-marker-${i}`}
              userData={{ landmarkType: 'path', milestoneIndex: i }}
            >
              <octahedronGeometry args={[MILESTONE_RADIUS, 0]} />
              <meshStandardMaterial
                color={isActiveMilestone ? ACTIVE_MILESTONE_COLOR : MILESTONE_COLOR}
                emissive={
                  isActiveMilestone ? ACTIVE_MILESTONE_COLOR : MILESTONE_COLOR
                }
                emissiveIntensity={isActiveMilestone ? 1.0 : 0.4}
                flatShading
              />
            </mesh>
            {/* Tiny soft point light at the active milestone. */}
            {isActiveMilestone && (
              <pointLight
                color={ACTIVE_MILESTONE_COLOR}
                intensity={0.8}
                distance={1.2}
              />
            )}
          </group>
        );
      })}
    </group>
  );
}

export default Path;
